// "Find a pallet" at the top of To inspect (F03 UF-02). The packhouse mobile
// audit's PM-QUA-14: on a 34-card, 8,638px phone page there was no way to find
// the pallet in front of you — no search box, no scan.
//
// Three ways in, one result — the page brings the matching card to the top
// (findInQueue + matchesFirst):
//  - type the last digits of the pallet number: a plain match over the rows
//    already loaded, live as you type;
//  - scan the label while the box has focus — a hand scanner types into it
//    like a keyboard, then Enter;
//  - scan with NOTHING focused: the wedge listener catches it at the page
//    (useScanWedge stands down while the operator types into any field).
// A scanned SSCC is not the pallet number, and the queue read carries no SSCC,
// so an SSCC that names no queue pallet by number is looked up once
// (palletBySscc, both SSCC lanes) and the box then holds the pallet's number.
// Until the queue has been read (first read in flight, or failed) the line
// says so — never "no pallet matches" or "not on the inspection queue" over
// a queue nobody has seen (readState, CLAUDE.md).
import { useRef, useState, type ReactNode } from 'react'
import { ScanLine, X } from 'lucide-react'
import { useScanWedge } from '@/hooks/useScanWedge'
import { normaliseSscc } from '@/lib/sscc'
import { playScanBeep } from '@/lib/tone'
import { readErrorText } from '@/lib/readError'
import { palletStatusLabel } from '@/lib/palletStatus'
import type { ReadState } from '@/lib/readState'
import PalletLink from './PalletLink'
import {
  findInQueue, elsewhereFound, scanShape, pallets,
  type QueueRow, type InspectionView,
} from './inspectionQueue'
import { palletBySscc, type SsccHit } from './inspectionReads'

/** A label worth an SSCC lookup: 18 digits once the ']C1' / '(00)' are gone. */
export function looksLikeSscc(text: string): boolean {
  return normaliseSscc(text).length === 18
}

type Note =
  | { kind: 'looking';  sscc: string }
  | { kind: 'none';     sscc: string }
  | { kind: 'offQueue'; sscc: string; hit: SsccHit }
  | { kind: 'named';    sscc: string; hit: SsccHit }
  | { kind: 'failed';   sscc: string; error: unknown }

export default function InspectionFind({
  query, onQuery, toInspect, queue, queueState, onView, canOpenPallet, lookup = palletBySscc,
}: {
  /** The box's text — the page holds it, and brings what it names to the top. */
  query:         string
  onQuery:       (next: string) => void
  /** To inspect's pallets, every chip (what the box searches). */
  toInspect:     readonly QueueRow[]
  /** Every queue row, so a pallet on another view is named, not "not found". */
  queue:         readonly QueueRow[]
  /** readState of the queue read. Anything but 'usable' means `queue` and
   *  `toInspect` are empty because nothing has been read, not because no
   *  pallet is waiting — so no "no match" / "not on the queue" claim. */
  queueState:    ReadState
  onView:        (v: InspectionView) => void
  canOpenPallet: boolean
  /** Scanned SSCC → its pallet; the real read unless a test hands in its own. */
  lookup?:       (raw: string) => Promise<SsccHit | null>
}) {
  const [note, setNote] = useState<Note | null>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  // A lookup answered after the box moved on (cleared, retyped, scanned
  // again) must not overwrite it.
  const seq = useRef(0)

  function edit(next: string) {
    seq.current += 1
    setNote(null)
    onQuery(next)
  }

  /** A text the queue doesn't name by number, shaped like an SSCC: look it
   *  up. A match on To inspect beeps, as a scan that found its card. */
  function needsLookup(text: string): boolean {
    if (!text) return false
    if (findInQueue(toInspect, text).length > 0) { playScanBeep(); return false }
    return findInQueue(queue, text).length === 0 && looksLikeSscc(text)
  }

  /** What an SSCC lookup found: nothing, a queue pallet (the box takes its
   *  number), or a pallet off the queue (named, with its status). */
  function land(hit: SsccHit | null, sscc: string) {
    if (!hit) { setNote({ kind: 'none', sscc }); return }
    // An unread queue can't say whether the pallet is on it: name the pallet
    // and its status, and let the box hold its number — its card comes to
    // the top once the queue is read.
    if (queueState !== 'usable') { setNote({ kind: 'named', sscc, hit }); onQuery(hit.pallet_id); return }
    if (!queue.some(p => p.pallet_id === hit.pallet_id)) { setNote({ kind: 'offQueue', sscc, hit }); return }
    setNote(null)
    onQuery(hit.pallet_id)
    if (toInspect.some(p => p.pallet_id === hit.pallet_id)) playScanBeep()
  }

  async function resolve(raw: string) {
    const text = raw.trim()
    const mine = ++seq.current
    setNote(null)
    onQuery(text)
    if (!needsLookup(text)) return
    const sscc = normaliseSscc(text)
    setNote({ kind: 'looking', sscc })
    try {
      const hit = await lookup(text)
      if (mine === seq.current) land(hit, sscc)
    } catch (e) {
      if (mine === seq.current) setNote({ kind: 'failed', sscc, error: e })
    }
  }

  // A scan with nothing focused: the box shows what was scanned, and comes
  // into view so the card it brought to the top is seen. The box handles its
  // own Enter.
  useScanWedge(() => null, raw => {
    boxRef.current?.scrollIntoView?.({ block: 'start' })
    void resolve(raw)
  }, { matchOnFlush: scanShape })

  return (
    <div ref={boxRef} className="space-y-1 scroll-mt-24">
      <div className="flex items-center gap-2 min-h-[44px] rounded-xl surface border hairline-strong pl-3">
        <ScanLine size={16} className="text-fg-muted shrink-0" aria-hidden />
        <input autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck={false}
          enterKeyHint="search"
          className="flex-1 min-w-0 self-stretch bg-transparent text-sm text-fg outline-none"
          aria-label="Find a pallet — scan its label or type the last digits"
          placeholder="Scan a label or type the last digits"
          value={query}
          onChange={e => edit(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void resolve(query) } }} />
        {note?.kind === 'looking' && <span className="text-[11px] text-fg-muted shrink-0">Looking…</span>}
        {query && (
          <button type="button" onClick={() => edit('')} aria-label="Clear the search"
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-fg-muted hover:text-fg shrink-0">
            <X size={18} aria-hidden />
          </button>
        )}
      </div>
      <FindLine query={query} note={note} toInspect={toInspect} queue={queue} queueState={queueState}
        onView={onView} canOpenPallet={canOpenPallet} />
    </div>
  )
}

type Line = { body: ReactNode; tone: string }
const AMBER = 'text-amber-300'

/** What an SSCC lookup's answer says, or null while there is none to show. */
function noteLine(note: Note | null, canOpenPallet: boolean): Line | null {
  if (!note) return null
  switch (note.kind) {
    case 'none':
      return { body: <>No pallet carries the label {note.sscc}.</>, tone: AMBER }
    case 'offQueue':
      return {
        body: <>Label {note.sscc} is pallet <PalletLink id={note.hit.pallet_id} canOpen={canOpenPallet} className="font-mono" />{' '}
          — it is not on the inspection queue ({palletStatusLabel(note.hit.status)}).</>,
        tone: AMBER,
      }
    case 'named':
      return {
        body: <>Label {note.sscc} is pallet <PalletLink id={note.hit.pallet_id} canOpen={canOpenPallet} className="font-mono" />{' '}
          ({palletStatusLabel(note.hit.status)}).</>,
        tone: 'text-fg-secondary',
      }
    case 'failed':
      return { body: <>Couldn't look up the label {note.sscc}: {readErrorText(note.error)}</>, tone: 'text-red-300' }
    default:
      return null
  }
}

/** Matches on another view: "Not here" alone sends the clerk looking for a
 *  pallet approved an hour ago, so name the view it is on, one tap away. */
function Elsewhere({ q, away, onView }: {
  q: string; away: ReturnType<typeof elsewhereFound>; onView: (v: InspectionView) => void
}) {
  const views = [...new Map(away.map(a => [a.view, a.viewName])).entries()]
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2">
      <span>Not on To inspect — {away.length === 1 ? `${away[0].id} is` : `${pallets(away.length)} matching “${q}” are`} on {views.map(([, n]) => n).join(' / ')}.</span>
      {views.map(([v, n]) => (
        <button key={v} type="button" onClick={() => onView(v)}
          className="min-h-[44px] px-2 font-semibold text-blue-300 hover:underline underline-offset-2">
          Open {n}
        </button>
      ))}
    </span>
  )
}

/** What the typed text matches — here, on another view, or nowhere. */
function queryLine(query: string, toInspect: readonly QueueRow[], queue: readonly QueueRow[], queueState: ReadState,
  onView: (v: InspectionView) => void): Line | null {
  const q = query.trim()
  if (q.length < 2) return null
  if (queueState === 'loading') return { body: <>The inspection queue is still loading — “{q}” is checked once it is in.</>, tone: 'text-fg-secondary' }
  if (queueState !== 'usable') return { body: <>Couldn't read the inspection queue, so “{q}” can't be checked against it.</>, tone: AMBER }
  const here = findInQueue(toInspect, q)
  if (here.length) {
    return { body: <>{pallets(here.length)} {here.length === 1 ? 'matches' : 'match'} “{q}” — at the top.</>, tone: 'text-fg-secondary' }
  }
  const away = elsewhereFound(queue, findInQueue(queue, q))
  if (away.length) return { body: <Elsewhere q={q} away={away} onView={onView} />, tone: AMBER }
  return { body: <>No pallet on the inspection queue matches “{q}”.</>, tone: AMBER }
}

/** What the line under the box says about the current text. */
function FindLine({ query, note, toInspect, queue, queueState, onView, canOpenPallet }: {
  query: string; note: Note | null; toInspect: readonly QueueRow[]; queue: readonly QueueRow[]; queueState: ReadState
  onView: (v: InspectionView) => void; canOpenPallet: boolean
}) {
  const line = noteLine(note, canOpenPallet)
    ?? (note?.kind === 'looking' ? null : queryLine(query, toInspect, queue, queueState, onView))
  if (!line) return null
  return <p role="status" className={`text-xs px-1 ${line.tone}`}>{line.body}</p>
}
